// The free-text fields of the vehicle card (answer 1): add, rename, reorder, remove.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sv } from '@aldorock-fordon/shared/sv';
import { api, ApiError } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { Table } from '../ui/Table.tsx';
import { TextField } from '../ui/Fields.tsx';

type Field = { id: string; key: string; label: string; sort: number };

function Rename({ f, onDone }: { f: Field; onDone: () => void }) {
  const [value, setValue] = useState(f.label);
  const save = useMutation({
    mutationFn: () =>
      api(`/admin/fields/${f.id}`, {
        method: 'PUT',
        body: JSON.stringify({ label: value.trim() }),
      }),
    onSuccess: onDone,
  });
  return (
    <form
      className="label-row row"
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim()) save.mutate();
      }}
    >
      <TextField
        id={`label-${f.key}`}
        label={`${sv.fieldsAdmin.label} ${f.key}`}
        value={value}
        onChange={setValue}
      />
      <button
        className="btn btn-quiet"
        type="submit"
        disabled={save.isPending || value.trim() === f.label}
      >
        {sv.fieldsAdmin.rename}
      </button>
    </form>
  );
}

export function FieldsAdminPage() {
  const h1 = usePage(sv.fieldsAdmin.title);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin-fields'], queryFn: () => api<Field[]>('/admin/fields') });
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [message, setMessage] = useState<string | null>(null);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['admin-fields'] });
    await qc.invalidateQueries({ queryKey: ['custom-fields'] });
    setMessage(sv.fieldsAdmin.saved);
  };
  const add = useMutation({
    mutationFn: () =>
      api('/admin/fields', {
        method: 'POST',
        body: JSON.stringify({ key: key.trim(), label: label.trim() }),
      }),
    onSuccess: async () => {
      setKey('');
      setLabel('');
      setError(undefined);
      await refresh();
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.errors[0]
          ? e.errors[0].message
          : sv.forms.invalid(sv.fieldsAdmin.key),
      ),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/fields/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });
  const move = useMutation({
    mutationFn: async (p: { id: string; dir: -1 | 1 }) => {
      const list = q.data ?? [];
      const i = list.findIndex((f) => f.id === p.id);
      const j = i + p.dir;
      if (i < 0 || j < 0 || j >= list.length) return;
      await api(`/admin/fields/${list[i]!.id}`, {
        method: 'PUT',
        body: JSON.stringify({ sort: list[j]!.sort }),
      });
      await api(`/admin/fields/${list[j]!.id}`, {
        method: 'PUT',
        body: JSON.stringify({ sort: list[i]!.sort }),
      });
    },
    onSuccess: refresh,
  });
  return (
    <>
      <PageHead
        help="fields"
        crumbs={[{ to: '/admin', label: sv.adminHome.title }, { label: sv.fieldsAdmin.title }]}
        title={sv.fieldsAdmin.title}
        titleRef={h1}
      />
      <p>{sv.fieldsAdmin.intro}</p>
      {message ? (
        <p className="notice" data-state="done" role="status">
          {message}
        </p>
      ) : null}
      <Table caption={sv.fieldsAdmin.title} isEmpty={(q.data?.length ?? 0) === 0}>
        <thead>
          <tr>
            <th scope="col">{sv.fieldsAdmin.key}</th>
            <th scope="col">{sv.fieldsAdmin.label}</th>
            <th scope="col">{sv.columns.action}</th>
          </tr>
        </thead>
        <tbody>
          {q.data?.map((f, i) => (
            <tr key={f.id}>
              <td className="num">{f.key}</td>
              <td>
                <Rename f={f} onDone={refresh} />
              </td>
              <td>
                <span className="actions-inline">
                  <button
                    className="btn btn-quiet"
                    type="button"
                    disabled={i === 0}
                    aria-label={sv.fieldsAdmin.up(f.label)}
                    onClick={() => move.mutate({ id: f.id, dir: -1 })}
                  >
                    ↑
                  </button>
                  <button
                    className="btn btn-quiet"
                    type="button"
                    disabled={i === (q.data?.length ?? 0) - 1}
                    aria-label={sv.fieldsAdmin.down(f.label)}
                    onClick={() => move.mutate({ id: f.id, dir: 1 })}
                  >
                    ↓
                  </button>
                  <button
                    className="btn btn-quiet"
                    type="button"
                    aria-label={sv.fieldsAdmin.remove(f.label)}
                    onClick={() => remove.mutate(f.id)}
                  >
                    {sv.fieldsAdmin.removeShort}
                  </button>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <form
        className="form-block"
        aria-labelledby="add-field"
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
      >
        <h2 className="form-heading" id="add-field">
          {sv.fieldsAdmin.add}
        </h2>
        <TextField
          id="field-key"
          label={sv.fieldsAdmin.key}
          help={sv.fieldsAdmin.keyHelp}
          value={key}
          onChange={setKey}
          error={error}
          required
        />
        <TextField
          id="field-label"
          label={sv.fieldsAdmin.label}
          value={label}
          onChange={setLabel}
          required
        />
        <p className="caption">{sv.fieldsAdmin.removeHelp}</p>
        <div className="form-actions">
          <button
            className="btn btn-primary"
            type="submit"
            disabled={add.isPending || !key.trim() || !label.trim()}
          >
            {sv.fieldsAdmin.addButton}
          </button>
        </div>
      </form>
    </>
  );
}
